import { useEffect, useMemo, useState } from "react";
import {
    Cpu,
    MapPin,
    Search,
    SlidersHorizontal,
    CheckCircle2,
    Wrench,
    Activity,
    BarChart3,
    Loader2,
    AlertCircle,
} from "lucide-react";
import { getAvailableMachines } from "../service/userService";

const statusStyle = {
    Available: "bg-green-100 text-green-600",
    "In Use": "bg-blue-100 text-blue-600",
    Maintenance: "bg-orange-100 text-orange-500",
};

const statusOptions = ["All Status", "Available", "In Use", "Maintenance"];

// API returns status as "AVAILABLE" / "IN_USE" / "MAINTENANCE" (uppercase, underscored)
// This maps it to the display-friendly labels used by statusStyle/statusOptions above.
function normalizeStatus(rawStatus) {
    switch (rawStatus) {
        case "AVAILABLE":
            return "Available";
        case "IN_USE":
            return "In Use";
        case "MAINTENANCE":
            return "Maintenance";
        default:
            return rawStatus || "Available";
    }
}

// Transforms a raw machine record from the API into the shape the table expects.
// NOTE: the API doesn't return `utilization` or `lastServiced` — defaulted below
// since the backend has no equivalent fields yet.
function mapApiMachine(m) {
    return {
        id: m.id,
        name: m.machineName || "Unnamed Machine",
        type: m.categoryName?.trim() || "Uncategorized",
        centre: m.facilityName || "—",
        status: normalizeStatus(m.status),
        utilization: 0,          // not provided by API
        lastServiced: "—",       // not provided by API
    };
}

export default function Machines() {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("All Status");
    const [centre, setCentre] = useState("All Centres");
    const [type, setType] = useState("All Types");

    // ===== Fetch machines on mount =====
    useEffect(() => {
        const fetchMachines = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getAvailableMachines();
                const rawList = Array.isArray(response) ? response : response?.data || [];

                setMachines(rawList.map(mapApiMachine));
            } catch (err) {
                console.error("Failed to fetch machines:", err);
                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to load machines. Please try again."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchMachines();
    }, []);

    const totalMachines = machines.length;

    const availableMachines = machines.filter((m) => m.status === "Available").length;
    const inUseMachines = machines.filter((m) => m.status === "In Use").length;
    const maintenanceMachines = machines.filter((m) => m.status === "Maintenance").length;

    const avgUtilization = machines.length
        ? Math.round(machines.reduce((sum, m) => sum + m.utilization, 0) / machines.length)
        : 0;

    const centreOptions = useMemo(
        () => ["All Centres", ...new Set(machines.map((m) => m.centre))],
        [machines]
    );

    const typeOptions = useMemo(
        () => ["All Types", ...new Set(machines.map((m) => m.type))],
        [machines]
    );

    const filtered = useMemo(() => {
        return machines.filter((m) => {
            const matchesQuery =
                query.trim() === "" ||
                m.name.toLowerCase().includes(query.toLowerCase()) ||
                m.type.toLowerCase().includes(query.toLowerCase());

            const matchesStatus = status === "All Status" || m.status === status;
            const matchesCentre = centre === "All Centres" || m.centre === centre;
            const matchesType = type === "All Types" || m.type === type;

            return matchesQuery && matchesStatus && matchesCentre && matchesType;
        });
    }, [machines, query, status, centre, type]);

    const hasActiveFilters =
        query || status !== "All Status" || centre !== "All Centres" || type !== "All Types";

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">

            {error && (
                <div className="m-6 mb-0 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5 p-6 border-b border-slate-100 bg-slate-50">

                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Total Machines</p>
                            <h2 className="text-3xl font-bold mt-2 text-slate-900">
                                {loading ? "—" : totalMachines}
                            </h2>
                        </div>

                        <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                            <Cpu className="text-cyan-600" size={24} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Available</p>
                            <h2 className="text-3xl font-bold mt-2 text-green-600">
                                {loading ? "—" : availableMachines}
                            </h2>
                        </div>

                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="text-green-600" size={24} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">In Use</p>
                            <h2 className="text-3xl font-bold mt-2 text-blue-600">
                                {loading ? "—" : inUseMachines}
                            </h2>
                        </div>

                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Activity className="text-blue-600" size={24} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Maintenance</p>
                            <h2 className="text-3xl font-bold mt-2 text-orange-600">
                                {loading ? "—" : maintenanceMachines}
                            </h2>
                        </div>

                        <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                            <Wrench className="text-orange-600" size={24} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-slate-500">Avg Utilization</p>
                            <h2 className="text-3xl font-bold mt-2 text-indigo-600">
                                {loading ? "—" : `${avgUtilization}%`}
                            </h2>
                        </div>

                        <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <BarChart3 className="text-indigo-600" size={24} />
                        </div>
                    </div>

                    <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${avgUtilization}%` }}
                        />
                    </div>
                </div>

            </div>
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-6 border-b border-slate-100">

                {/* Filters */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-wrap">
                    <div className="relative">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search machine, type..."
                            disabled={loading}
                            className="pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition w-full sm:w-56 disabled:opacity-50"
                        />
                    </div>

                    <select
                        value={centre}
                        onChange={(e) => setCentre(e.target.value)}
                        disabled={loading}
                        className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition disabled:opacity-50"
                    >
                        {centreOptions.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>

                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        disabled={loading}
                        className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition disabled:opacity-50"
                    >
                        {typeOptions.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>

                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        disabled={loading}
                        className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition disabled:opacity-50"
                    >
                        {statusOptions.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>

                    {hasActiveFilters && (
                        <button
                            onClick={() => {
                                setQuery("");
                                setStatus("All Status");
                                setCentre("All Centres");
                                setType("All Types");
                            }}
                            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 px-3 py-2 transition"
                        >
                            <SlidersHorizontal size={14} />
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
                <table className="w-full min-w-[860px] text-sm">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 bg-slate-50">
                            <th className="px-6 py-3 font-semibold">Machine</th>
                            <th className="px-6 py-3 font-semibold">Type</th>
                            <th className="px-6 py-3 font-semibold">Centre</th>
                            <th className="px-6 py-3 font-semibold">Utilization</th>
                            <th className="px-6 py-3 font-semibold">Last Serviced</th>
                            <th className="px-6 py-3 font-semibold">Status</th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 size={18} className="animate-spin" />
                                        Loading machines...
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && filtered.map((machine) => (
                            <tr
                                key={machine.id}
                                className="hover:bg-slate-50 transition-colors"
                            >
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center shrink-0">
                                            <Cpu size={16} />
                                        </div>
                                        <span className="font-semibold text-slate-900">
                                            {machine.name}
                                        </span>
                                    </div>
                                </td>

                                <td className="px-6 py-4 text-slate-600">
                                    {machine.type}
                                </td>

                                <td className="px-6 py-4 text-slate-600">
                                    <div className="flex items-center gap-1.5">
                                        <MapPin
                                            size={14}
                                            className="text-slate-400"
                                        />
                                        {machine.centre}
                                    </div>
                                </td>

                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2 w-32">
                                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-indigo-500"
                                                style={{
                                                    width: `${machine.utilization}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="text-slate-500 text-xs w-8 shrink-0">
                                            {machine.utilization}%
                                        </span>
                                    </div>
                                </td>

                                <td className="px-6 py-4 text-slate-500">
                                    {machine.lastServiced}
                                </td>

                                <td className="px-6 py-4">
                                    <span
                                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle[machine.status] || "bg-slate-100 text-slate-500"}`}
                                    >
                                        {machine.status}
                                    </span>
                                </td>
                            </tr>
                        ))}

                        {!loading && !error && filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-6 py-12 text-center text-slate-400"
                                >
                                    No machines match your filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}